import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Chatbot from "@/components/Chatbot";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/our-story", label: "Our Story" },
  { to: "/legacy-films", label: "Legacy Films" },
  { to: "/funeral-services-sydney", label: "Funeral Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/gift", label: "Gift a Legacy" },
  { to: "/blog", label: "Stories" },
  { to: "/faqs", label: "FAQs" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/95 backdrop-blur-md shadow-[0_2px_16px_rgba(0,0,0,0.04)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-2 md:py-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_legacy-stories-hub/artifacts/8a4y2wc7_png-04.webp"
            alt="Family Legacy Films"
            className="h-20 md:h-28 w-auto object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.slice(0, 8).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.15em] font-medium transition-colors ${
                  isActive ? "text-primary" : scrolled ? "text-secondary hover:text-primary" : "text-white/90 hover:text-primary"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-book-cta" className="hidden md:inline-flex btn-primary text-sm">
            Book a Free Chat
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden p-2 ${scrolled ? "text-secondary" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-border-warm">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-secondary hover:text-primary uppercase text-sm tracking-widest"
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary text-sm mt-2" data-testid="mobile-nav-book-cta">
              Book a Free Chat
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function StickyBookBar() {
  return (
    <a
      href="tel:+61460017777"
      className="fixed bottom-6 right-6 z-40 hidden md:inline-flex items-center gap-2 bg-secondary text-white px-5 py-3 rounded-full shadow-lg hover:bg-secondary-hover transition-all"
      data-testid="sticky-call-cta"
    >
      <Phone size={16} /> <span className="text-sm">Call: 0460 017 777</span>
    </a>
  );
}

export function Footer() {
  return (
    <footer className="bg-secondary text-white pt-24 pb-10 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-4 gap-12">
        <div>
          <h3 className="font-serif text-2xl mb-4 text-white">Family <span style={{ color: "#C9A961" }}>Legacy</span></h3>
          <p className="text-white/70 text-sm leading-relaxed">
            Cinematic legacy documentaries and dignified funeral videography across Sydney &amp; Canberra.
          </p>
        </div>
        <div>
          <p className="eyebrow text-primary mb-4">Explore</p>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/legacy-films" className="hover:text-primary">Legacy Films</Link></li>
            <li><Link to="/funeral-services-sydney" className="hover:text-primary">Funeral Services Sydney</Link></li>
            <li><Link to="/funeral-services-canberra" className="hover:text-primary">Funeral Services Canberra</Link></li>
            <li><Link to="/legacy-films#packages" className="hover:text-primary">Packages</Link></li>
            <li><Link to="/gift" className="hover:text-primary">Gift a Legacy</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow text-primary mb-4">Company</p>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/our-story" className="hover:text-primary">Our Story</Link></li>
            <li><Link to="/gallery" className="hover:text-primary">Gallery</Link></li>
            <li><Link to="/blog" className="hover:text-primary">Stories</Link></li>
            <li><Link to="/faqs" className="hover:text-primary">FAQs</Link></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow text-primary mb-4">Contact</p>
          <ul className="space-y-2 text-white/70 text-sm">
            <li>Sydney &amp; Canberra, Australia</li>
            <li><a href="mailto:info@familylegacyfilms.com.au" className="hover:text-primary">info@familylegacyfilms.com.au</a></li>
            <li><a href="tel:+61460017777" className="hover:text-primary">0460 017 777</a></li>
            <li className="pt-2"><Link to="/contact" className="link-underline">Book a Free Chat →</Link></li>
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-14 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-white/40 text-xs">© {new Date().getFullYear()} Family Legacy Films. All rights reserved.</p>
        <div className="flex gap-6 text-white/40 text-xs">
          <Link to="/privacy" className="hover:text-primary">Privacy Policy</Link>
          <Link to="/admin" className="hover:text-primary" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <StickyBookBar />
      <Chatbot />
      <Footer />
    </div>
  );
}
